import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ChartBarIcon,
  UserIcon,
  ClockIcon,
  ArrowTrendingUpIcon as TrendingUpIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { dashboardApi, participantApi, insightsApi } from '../services/api';
import ReactMarkdown from 'react-markdown';

function Dashboard() {
  const navigate = useNavigate();
  const [participant, setParticipant] = useState(null);
  const [overview, setOverview] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  
  // Estado para la IA
  const [aiInsight, setAiInsight] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  useEffect(() => {
    // Buscar la información de sesión activa guardada en localStorage
    const activeParticipantStr = localStorage.getItem('activeParticipant');
    
    // Si existe la sesión, procesar y cargar los datos correspondientes
    if (activeParticipantStr) {
      // Parsear la cadena JSON a un objeto JS
      const activeParticipant = JSON.parse(activeParticipantStr);
      // Guardar el participante en el state de selección y perfil
      setSelectedParticipant(activeParticipant);
      // Ejecutar la carga de los resultados específicos del participante real desde el backend
      loadData(activeParticipant.id);
    } else {
      // Si por alguna razón intenta entrar sin sesión, redirigir a login de inmediato
      navigate('/login');
    }
  }, [navigate]);

  const loadData = async (participantId) => {
    try {
      setLoading(true);

      // Get participant data
      const participantResponse = await participantApi.getById(participantId);
      setParticipant(participantResponse.data);

      // Get dashboard overview
      const overviewResponse = await dashboardApi.getOverview(participantId);
      setOverview(overviewResponse.data);

      // Get recent test sessions
      const sessionsResponse = await participantApi.getTestSessions(participantId);
      const sessions = sessionsResponse.data;
      setRecentSessions(sessions.slice(0, 5)); // Show last 5 sessions

      // Intentar cargar insight de IA si ya existe para la sesión más reciente
      if (sessions.length > 0) {
        try {
          const insightResp = await insightsApi.getInsight(participantId, 'dashboard', sessions[0].id);
          if (insightResp.data) {
            setAiInsight(insightResp.data.insight_text);
          }
        } catch (e) {
          // Si es 404 (no existe aún), simplemente ignoramos
          if (e.response && e.response.status !== 404) {
            console.error('Error verificando IA:', e);
          }
        }
      }

    } catch (error) {
      toast.error('Error al cargar los datos del dashboard');
      console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInsight = async () => {
    if (!recentSessions || recentSessions.length === 0) return;
    try {
      setLoadingAi(true);
      const response = await insightsApi.generateInsight(selectedParticipant.id, 'dashboard', recentSessions[0].id);
      setAiInsight(response.data.insight_text);
      toast.success('¡Análisis generado exitosamente!');
    } catch (error) {
      console.error('Error generando IA:', error);
      toast.error('No se pudo generar el análisis. Verifica que la API Key esté configurada.');
    } finally {
      setLoadingAi(false);
    }
  };

  const getMaturityLevelColor = (score) => {
    if (score >= 76) return 'maturity-76-100';
    if (score >= 51) return 'maturity-51-75';
    if (score >= 26) return 'maturity-26-50';
    return 'maturity-1-25';
  };

  const getMaturityLevelText = (score) => {
    if (score >= 76) return 'Optimizado / Mentor';
    if (score >= 51) return 'Competente / Consistente';
    if (score >= 26) return 'En Desarrollo';
    return 'Inicial / Reactivo';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-8 text-white">
        <h1 className="text-3xl font-bold mb-2">
          Bienvenido, {participant?.name}
        </h1>
        <p className="text-lg opacity-90">
          Aquí puedes ver tu progreso de autoevaluación y acceder a todas las herramientas
        </p>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-6 card-shadow">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Sesiones Totales</p>
                <p className="text-2xl font-bold text-gray-900">{overview.total_sessions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 card-shadow">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Sesiones Recientes</p>
                <p className="text-2xl font-bold text-gray-900">{overview.recent_sessions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 card-shadow">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <UserIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Promedio General</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Math.round((overview.avg_mbti_score + overview.avg_via_score + overview.avg_enneagram_score + overview.avg_maturity_score) / 4)}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 card-shadow">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <TrendingUpIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Mejor Área</p>
                <p className="text-lg font-bold text-gray-900">
                  {overview.avg_maturity_score > overview.avg_mbti_score ? 'Madurez' : 'Personalidad'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg p-6 card-shadow">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/tests')}
            className="flex items-center justify-center p-4 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
            Realizar Tests
          </button>
          <button
            onClick={() => navigate('/progress')}
            className="flex items-center justify-center p-4 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
          >
            <TrendingUpIcon className="h-5 w-5 mr-2" />
            Ver Progreso
          </button>
          <button
            onClick={() => navigate('/comparison')}
            className="flex items-center justify-center p-4 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <ArrowsRightLeftIcon className="h-5 w-5 mr-2" />
            Comparar Sesiones
          </button>
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-white rounded-lg p-6 card-shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Sesiones Recientes</h2>
          <button
            onClick={() => navigate('/results')}
            className="text-indigo-600 hover:text-indigo-500 font-medium flex items-center"
          >
            Ver Todas <ArrowRightIcon className="h-4 w-4 ml-1" />
          </button>
        </div>

        {recentSessions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No tienes sesiones recientes.</p>
            <button
              onClick={() => navigate('/tests')}
              className="mt-4 text-indigo-600 hover:text-indigo-500 font-medium"
            >
              Realizar tu primera evaluación
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {recentSessions.map((session) => (
              <div key={session.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    {/* Convertir la fecha UTC de SQLite a un formato que JS entienda como UTC añadiendo 'Z' */}
                    <p className="font-medium text-gray-900">
                      {new Date(session.session_date.replace(' ', 'T') + 'Z').toLocaleDateString('es-ES')}
                    </p>
                    {/* Lista de tests completados con tipo y puntaje */}
                    <p className="text-sm text-gray-600">
                      Tests completados:{' '}
                      {session.completed_tests && session.completed_tests.length > 0
                        ? session.completed_tests.map((test, i) => {
                            // Buscar el detalle del test (tipo y puntaje) según corresponda
                            let detail = '';
                            if (test === 'MBTI' && session.mbti)       detail = ` (${session.mbti.type} · ${session.mbti.score}%)`;
                            if (test === 'VIA' && session.via)          detail = ` (${session.via.score}%)`;
                            if (test === 'Eneagrama' && session.enneagram) detail = ` Tipo ${session.enneagram.type} · ${session.enneagram.score}%)`;
                            return <span key={i}>{i > 0 ? ', ' : ''}<strong>{test}</strong>{detail}</span>;
                          })
                        : 'Ninguno'
                      }
                    </p>
                  </div>
                  <div className="text-right">
                    {/* Convertir la fecha UTC de SQLite y mostrar la hora en la zona local del usuario */}
                    <p className="text-sm text-gray-400">
                      {new Date(session.session_date.replace(' ', 'T') + 'Z').toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Assistant Section */}
      {recentSessions.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-6 card-shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-indigo-900 flex items-center">
              <SparklesIcon className="h-6 w-6 text-indigo-600 mr-2" />
              Análisis Asistido por IA
            </h2>
            {!aiInsight && !loadingAi && (
              <button
                onClick={handleGenerateInsight}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center text-sm shadow-sm hover:shadow-md"
              >
                <SparklesIcon className="h-4 w-4 mr-2" />
                Generar Análisis
              </button>
            )}
          </div>
          
          {loadingAi ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-4"></div>
              <p className="text-indigo-600 animate-pulse">La Inteligencia Artificial está analizando tu perfil...</p>
            </div>
          ) : aiInsight ? (
            <div className="prose prose-indigo max-w-none text-gray-800 bg-white bg-opacity-60 p-5 rounded-lg">
              <ReactMarkdown>{aiInsight}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <p>Presiona el botón para que nuestra Inteligencia Artificial analice tus últimos resultados y te dé recomendaciones personalizadas.</p>
            </div>
          )}
        </div>
      )}

      {/* Maturity Scores Overview */}
      {overview && (
        <div className="bg-white rounded-lg p-6 card-shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Niveles de Madurez</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Inteligencia Emocional</h3>
              <div className="flex items-center">
                <div className={`flex-1 bg-gray-200 rounded-full h-3 ${getMaturityLevelColor(overview.avg_maturity_score)}`}>
                  <div
                    className="bg-indigo-600 h-3 rounded-full progress-bar"
                    style={{ width: `${overview.avg_maturity_score}%` }}
                  ></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {overview.avg_maturity_score}%
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {getMaturityLevelText(overview.avg_maturity_score)}
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Personalidad</h3>
              <div className="flex items-center">
                <div className={`flex-1 bg-gray-200 rounded-full h-3 ${getMaturityLevelColor(overview.avg_mbti_score)}`}>
                  <div
                    className="bg-purple-600 h-3 rounded-full progress-bar"
                    style={{ width: `${overview.avg_mbti_score}%` }}
                  ></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {overview.avg_mbti_score}%
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {getMaturityLevelText(overview.avg_mbti_score)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for icons
const ClipboardDocumentListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);

const ArrowsRightLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);

export default Dashboard;